import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Inbox, Check } from "lucide-react";
import api from "@/lib/api";
import DashboardShell from "@/components/layout/DashboardShell";
import { adminMenu } from "@/config/menus";
import { PageHeader } from "@/components/KpiCard";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { LoadingScreen, EmptyState } from "@/components/common";

const STATUS = { NEW: "bg-brand-100 text-brand-600", CONTACTED: "bg-emerald-100 text-emerald-700", CLOSED: "bg-navy-100 text-navy-500" };

export default function AdminEnquiries() {
  const [items, setItems] = useState(null);

  const load = () => api.get("/admin/enquiries").then((r) => setItems(r.data));
  useEffect(() => { load().catch(() => toast.error("Failed to load")); }, []);
  if (!items) return <DashboardShell menu={adminMenu} title="Enquiries"><LoadingScreen /></DashboardShell>;

  const setStatus = async (e, status) => {
    setItems(items.map((x) => (x.id === e.id ? { ...x, status } : x)));
    try { await api.put(`/admin/enquiries/${e.id}`, { status }); toast.success(`Marked ${status.toLowerCase()}`); }
    catch { toast.error("Failed"); load(); }
  };

  return (
    <DashboardShell menu={adminMenu} title="Enquiries">
      <PageHeader title="Contact Enquiries" subtitle="Enquiries submitted through the public Contact page." />
      {items.length === 0 ? (
        <EmptyState icon={Inbox} title="No enquiries yet" hint="Submissions from the Contact page will appear here." />
      ) : (
        <div className="space-y-3" data-testid="enquiries-list">
          {items.map((e) => (
            <div key={e.id} className="bg-white border border-navy-200 rounded-xl p-5" data-testid={`enquiry-${e.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-heading font-semibold text-navy-900">{e.name}</p>
                    <Badge variant="outline" className="border-navy-300 text-navy-600">{e.user_type}</Badge>
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${STATUS[e.status] || STATUS.NEW}`}>{e.status}</span>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-navy-600">
                    <a href={`tel:${e.mobile}`} className="flex items-center gap-1.5 hover:text-brand"><Phone size={14} /> {e.mobile}</a>
                    {e.email && <a href={`mailto:${e.email}`} className="flex items-center gap-1.5 hover:text-brand"><Mail size={14} /> {e.email}</a>}
                    {e.city && <span className="flex items-center gap-1.5"><MapPin size={14} /> {e.city}</span>}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="border-navy-300" onClick={() => setStatus(e, "CONTACTED")} data-testid={`enquiry-contacted-${e.id}`}><Check size={14} className="mr-1" /> Contacted</Button>
                  <Button size="sm" variant="outline" className="border-navy-300 text-navy-500" onClick={() => setStatus(e, "CLOSED")} data-testid={`enquiry-closed-${e.id}`}>Close</Button>
                </div>
              </div>
              {e.requirement && <p className="mt-3 text-sm text-navy-700"><span className="font-semibold">Requirement:</span> {e.requirement}</p>}
              {e.message && <p className="mt-1 text-sm text-navy-600">{e.message}</p>}
              <p className="mt-2 text-xs text-navy-400">{new Date(e.created_at).toLocaleString("en-IN")}</p>
            </div>
          ))}
        </div>
      )}
    </DashboardShell>
  );
}
